/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import {
  CForm, CRow, CCol, CFormInput, CFormLabel, CButton, CSpinner, CAlert, CFormSelect
} from '@coreui/react';
import { CIcon } from '@coreui/icons-react';
import { cilSave, cilX } from '@coreui/icons';
import PropTypes from 'prop-types';
import { useTranslation } from '../../hooks/useTranslation';
import { getShipmentDropdown, getSupplier } from '../../services/endpoints';
import { formatDateForInput, formatDateForBackend } from '../../helpers/dateHelper';
import '../OrderInputs/OrderInputs.scss';
import SearchableSelect from '../SearchableSelect/SearchableSelect';
import { splitFromRaw, toScaledIntegerStringFromParts } from '../../helpers/quantity';

const ShipmentInputs = ({
  onSubmit,
  onCancel,
  isLoading = false,
  submitResponse = null,
  initialData = null,
  isEdit = false
}) => {
  const [formData, setFormData] = useState({
    OpsShipmentStatusGuid: '',
    PrdUnitGuid: '',
    BillingCompany: '',
    ShipmentDatetime: '',
    DeliveryNo: '',
    Description: '',
    ProductShippedQuantity: '',
    NetWeight: '',
    GrossWeight: '',
    Lot: '',
    RollCount: '',
    OpsSupplierGuid: '',
    OpsOrderGuid: ''
  });
  const [errors, setErrors] = useState({});
  const { t } = useTranslation();
  const [shipmentStatusOptions, setShipmentStatusOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const [orderOptions, setOrderOptions] = useState([]);
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [dropdownLoading, setDropdownLoading] = useState(true);
  const [dropdownError, setDropdownError] = useState(null);

  // Separate decimal inputs for shipped quantity (whole and decimal parts)
  const [quantityInputs, setQuantityInputs] = useState({
    shippedWhole: '',
    shippedDecimal: '',
    netWeightWhole: '',
    netWeightDecimal: '',
    grossWeightWhole: '',
    grossWeightDecimal: ''
  });

  // splitting handled by helpers

  const buildOrderOptionFromInitialData = () => {
    if (!initialData?.OrderGuid) return null;

    return {
      OpsOrderGuid: String(initialData.OrderGuid || ''),
      OrderNo: initialData.OrderNo || '',
      OpsCustomerName: initialData.OpsCustomerName || '',
      WeaveCode: initialData.WeaveCode || '',
      YarnName: initialData.YarnName || '',
      YarnCode: initialData.YarnCode || ''
    };
  };

  const mergeSelectedOrderOption = (incomingOrders, previousOrders, selectedGuid) => {
    const selectedGuidStr = String(selectedGuid || '').trim();
    if (!selectedGuidStr) return incomingOrders;

    const hasSelectedInIncoming = incomingOrders.some(
      item => String(item?.OpsOrderGuid || '') === selectedGuidStr
    );
    if (hasSelectedInIncoming) return incomingOrders;

    const selectedInPrevious = previousOrders.find(
      item => String(item?.OpsOrderGuid || '') === selectedGuidStr
    );
    if (selectedInPrevious) {
      return [selectedInPrevious, ...incomingOrders];
    }

    const selectedFromInitialData = buildOrderOptionFromInitialData();
    if (selectedFromInitialData && String(selectedFromInitialData.OpsOrderGuid) === selectedGuidStr) {
      return [selectedFromInitialData, ...incomingOrders];
    }

    return incomingOrders;
  };

  const fetchOrderOptionsByOrderNo = async (searchTerm) => {
    const sessionUserId = localStorage.getItem('sessionUserId');
    const trimmedSearchTerm = (searchTerm || '').trim();

    if (!trimmedSearchTerm) {
      setOrderOptions(prev => mergeSelectedOrderOption([], prev, formData.OpsOrderGuid));
      return;
    }

    try {
      const response = await getShipmentDropdown({
        SessionUserId: sessionUserId,
        OrderNo: trimmedSearchTerm
      });
      const data = response?.data || {};
      const incomingOrders = data.Orders || [];
      setOrderOptions(prev => mergeSelectedOrderOption(incomingOrders, prev, formData.OpsOrderGuid));
    } catch (err) {
      setOrderOptions(prev => mergeSelectedOrderOption([], prev, formData.OpsOrderGuid));
    }
  };



  useEffect(() => {
    if (initialData) {
      // Initialize split decimal inputs from initial data (scaled integer aware)
      const shippedSplit = splitFromRaw(initialData.ProductShippedQuantity);
      const netWeightSplit = splitFromRaw(initialData.NetWeight);
      const grossWeightSplit = splitFromRaw(initialData.GrossWeight);

      setQuantityInputs({
        shippedWhole: shippedSplit.whole,
        shippedDecimal: shippedSplit.decimal,
        netWeightWhole: netWeightSplit.whole,
        netWeightDecimal: netWeightSplit.decimal,
        grossWeightWhole: grossWeightSplit.whole,
        grossWeightDecimal: grossWeightSplit.decimal
      });

      setFormData({
        OpsShipmentStatusGuid: initialData.OpsShipmentStatusGuid || '',
        PrdUnitGuid: initialData.PrdUnitGuid || '',
        BillingCompany: initialData.BillingCompany || '',
        ShipmentDatetime: initialData.ShipmentDatetime || '',
        DeliveryNo: initialData.DeliveryNo || '',
        Description: initialData.Description || '',
        ProductShippedQuantity: initialData.ProductShippedQuantity || '',
        NetWeight: initialData.NetWeight || '',
        GrossWeight: initialData.GrossWeight || '',
        Lot: initialData.Lot || '',
        RollCount: initialData.RollCount ?? '',
        OpsSupplierGuid: initialData.OpsSupplierGuid || '',
        OpsOrderGuid: initialData.OrderGuid || ''
      });
    }
  }, [initialData]);

  // Keep scaled decimal fields in sync with split inputs
  useEffect(() => {
    const buildCombined = (whole, decimal) => {
      const hasAny = whole !== '' || decimal !== '';
      return hasAny
        ? `${whole || '0'}.${(decimal || '00').padEnd(2, '0').slice(0, 2)}`
        : '';
    };

    setFormData(prev => ({
      ...prev,
      ProductShippedQuantity: buildCombined(quantityInputs.shippedWhole, quantityInputs.shippedDecimal),
      NetWeight: buildCombined(quantityInputs.netWeightWhole, quantityInputs.netWeightDecimal),
      GrossWeight: buildCombined(quantityInputs.grossWeightWhole, quantityInputs.grossWeightDecimal)
    }));
  }, [
    quantityInputs.shippedWhole,
    quantityInputs.shippedDecimal,
    quantityInputs.netWeightWhole,
    quantityInputs.netWeightDecimal,
    quantityInputs.grossWeightWhole,
    quantityInputs.grossWeightDecimal
  ]);

  useEffect(() => {
    if (submitResponse?.Result?.ErrorCode === 0) {
      setTimeout(() => {
        if (!isEdit) {
          setFormData({
            OpsShipmentStatusGuid: '',
            PrdUnitGuid: '',
            BillingCompany: '',
            ShipmentDatetime: '',
            DeliveryNo: '',
            Description: '',
            ProductShippedQuantity: '',
            NetWeight: '',
            GrossWeight: '',
            Lot: '',
            RollCount: '',
            OpsSupplierGuid: '',
            OpsOrderGuid: ''
          });
          setQuantityInputs({
            shippedWhole: '',
            shippedDecimal: '',
            netWeightWhole: '',
            netWeightDecimal: '',
            grossWeightWhole: '',
            grossWeightDecimal: ''
          });
        }
      }, 2000);
    }
  }, [submitResponse, isEdit]);

  useEffect(() => {
    const fetchDropdown = async () => {
      setDropdownLoading(true);
      setDropdownError(null);
      try {
        const sessionUserId = localStorage.getItem('sessionUserId');
        const [shipmentDropdownResponse, supplierResponse] = await Promise.all([
          getShipmentDropdown({ SessionUserId: sessionUserId }),
          getSupplier({ SessionUserId: sessionUserId })
        ]);
        const data = shipmentDropdownResponse?.data || {};
        const supplierData = supplierResponse?.data || {};
        setShipmentStatusOptions(data.ShipmentStatuses || []);
        setUnitOptions(data.PrdUnits || []);
        setSupplierOptions(supplierData.SupplierList || []);
        const initialOrderOption = buildOrderOptionFromInitialData();
        setOrderOptions(initialOrderOption ? [initialOrderOption] : []);
      } catch (err) {
        setDropdownError('Dropdown verileri alınamadı.');
        setShipmentStatusOptions([]);
        setUnitOptions([]);
        setSupplierOptions([]);
        const initialOrderOption = buildOrderOptionFromInitialData();
        setOrderOptions(initialOrderOption ? [initialOrderOption] : []);
      } finally {
        setDropdownLoading(false);
      }
    };
    fetchDropdown();
  }, [initialData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.OpsOrderGuid || !formData.OpsOrderGuid.trim()) newErrors.OpsOrderGuid = t('orderGuidRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getFieldConfig = (field) => {
    if (field === 'netWeight') {
      return { wholeKey: 'netWeightWhole', decimalKey: 'netWeightDecimal', errorKey: 'NetWeight', decimalInputId: 'netWeightDecimal' };
    }
    if (field === 'grossWeight') {
      return { wholeKey: 'grossWeightWhole', decimalKey: 'grossWeightDecimal', errorKey: 'GrossWeight', decimalInputId: 'grossWeightDecimal' };
    }
    return { wholeKey: 'shippedWhole', decimalKey: 'shippedDecimal', errorKey: 'ProductShippedQuantity', decimalInputId: 'shippedDecimal' };
  };

  const handleQuantityInputChange = (field, part, value) => {
    const { wholeKey, decimalKey, errorKey } = getFieldConfig(field);
    if (part === 'whole') {
      if (!/^\d*$/.test(value)) return;
    }
    if (part === 'decimal') {
      if (!/^\d{0,2}$/.test(value)) return;
    }
    setQuantityInputs(prev => ({ ...prev, [part === 'whole' ? wholeKey : decimalKey]: value }));
    if (errors[errorKey]) {
      setErrors(prev => ({ ...prev, [errorKey]: '' }));
    }
  };

  const handleQuantityKeyDown = (e, field, part) => {
    const { decimalInputId, decimalKey } = getFieldConfig(field);
    if (part === 'whole') {
      const isDotOrCommaKey = e.key === '.' || e.key === ',' || e.keyCode === 190 || e.keyCode === 110 || e.keyCode === 188;
      if (isDotOrCommaKey) {
        e.preventDefault();
        const decimalInput = document.getElementById(decimalInputId);
        if (decimalInput) {
          decimalInput.focus();
          const valueLength = decimalInput.value ? decimalInput.value.length : 0;
          try { decimalInput.setSelectionRange(valueLength, valueLength); } catch (err) {}
        }
        return;
      }
    }
    if ([8, 9, 27, 13, 46, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode === 67 && e.ctrlKey === true) ||
      (e.keyCode === 86 && e.ctrlKey === true) ||
      (e.keyCode === 88 && e.ctrlKey === true)) {
      return;
    }
    if (part === 'decimal') {
      const currentValue = quantityInputs[decimalKey] || e.target.value;
      const selectionStart = e.target.selectionStart;
      const selectionEnd = e.target.selectionEnd;
      if (currentValue.length >= 2 && selectionStart === selectionEnd) {
        e.preventDefault();
        return;
      }
    }
    if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
      e.preventDefault();
    }
  };

  const handleQuantityFocus = (field, part) => {
    if (part === 'whole') {
      const { wholeKey } = getFieldConfig(field);
      const input = document.getElementById(wholeKey);
      if (input && input.value) {
        setTimeout(() => input.select(), 0);
      }
    }
  };

  const handleQuantityBlur = (field, part) => {
    const { wholeKey, decimalKey } = getFieldConfig(field);
    if (part === 'decimal') {
      const currentValue = quantityInputs[decimalKey];
      if (currentValue !== '' && currentValue.length === 1) {
        setQuantityInputs(prev => ({ ...prev, [decimalKey]: currentValue.padEnd(2, '0') }));
      } else if (currentValue === '' && quantityInputs[wholeKey]) {
        setQuantityInputs(prev => ({ ...prev, [decimalKey]: '00' }));
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const sessionUserId = localStorage.getItem('sessionUserId');
      const userId = sessionUserId || userData?.sessionUserId;
      if (!userId || userId === 'undefined') {
        setErrors({ general: 'User session not found. Please login again.' });
        return;
      }
      const submitData = {
        SessionUserId: String(userId),
        OpsShipmentStatusGuid: formData.OpsShipmentStatusGuid.trim(),
        PrdUnitGuid: formData.PrdUnitGuid.trim(),
        BillingCompany: formData.BillingCompany.trim(),
        ShipmentDatetime: formatDateForBackend(formData.ShipmentDatetime) || '',
        DeliveryNo: formData.DeliveryNo.trim(),
        Description: formData.Description.trim(),
        ProductShippedQuantity: toScaledIntegerStringFromParts(quantityInputs.shippedWhole, quantityInputs.shippedDecimal),
        NetWeight: (quantityInputs.netWeightWhole !== '' || quantityInputs.netWeightDecimal !== '')
          ? toScaledIntegerStringFromParts(quantityInputs.netWeightWhole, quantityInputs.netWeightDecimal)
          : '',
        GrossWeight: (quantityInputs.grossWeightWhole !== '' || quantityInputs.grossWeightDecimal !== '')
          ? toScaledIntegerStringFromParts(quantityInputs.grossWeightWhole, quantityInputs.grossWeightDecimal)
          : '',
        Lot: formData.Lot,
        RollCount: String(formData.RollCount || '').trim(),
        OpsSupplierGuid: formData.OpsSupplierGuid.trim(),
        OpsOrderGuid: formData.OpsOrderGuid.trim()
      };
      if (isEdit && initialData?.ShipmentGuid) {
        submitData.ShipmentId = initialData.ShipmentGuid;
      }
      onSubmit(submitData);
    }
  };

  const handleCancel = () => {
    if (!isEdit) {
      setFormData({
        OpsShipmentStatusGuid: '',
        PrdUnitGuid: '',
        BillingCompany: '',
        ShipmentDatetime: '',
        DeliveryNo: '',
        Description: '',
        ProductShippedQuantity: '',
        NetWeight: '',
        GrossWeight: '',
        Lot: '',
        RollCount: '',
        OpsSupplierGuid: '',
        OpsOrderGuid: ''
      });
      setQuantityInputs({
        shippedWhole: '',
        shippedDecimal: '',
        netWeightWhole: '',
        netWeightDecimal: '',
        grossWeightWhole: '',
        grossWeightDecimal: ''
      });
    }
    setErrors({});
    onCancel();
  };

  const getButtonText = () => {
    if (isLoading) {
      return isEdit ? t('updating') : t('creating');
    }
    return isEdit ? t('updateShipment') : t('createShipment');
  };

  return (
    <div className="order-inputs-container">
      {errors.general && (
        <CAlert color="danger" dismissible onClose={() => setErrors(prev => ({ ...prev, general: '' }))}>
          {errors.general}
        </CAlert>
      )}
      {submitResponse?.Result?.ErrorCode !== 0 && submitResponse?.Result?.ErrorDescription && (
        <CAlert color="danger">{submitResponse.Result.ErrorDescription}</CAlert>
      )}
      <CForm onSubmit={handleSubmit} noValidate>
        <CRow className="mb-3">
          <CCol md={6}>
            <CFormLabel htmlFor="OpsOrderGuid">{t('order')}</CFormLabel>
            <SearchableSelect
              id="OpsOrderGuid"
              name="OpsOrderGuid"
              options={orderOptions.map(item => ({
                ...item,
                OpsOrderGuid: String(item.OpsOrderGuid || ''),
                Name: [item.OrderNo, item.OpsCustomerName, item.OpsSupplierName, item.WeaveCode, item.YarnName, item.YarnCode].filter(Boolean).join(' - ')
              }))}
              value={formData.OpsOrderGuid}
              onChange={(selectedValue) => handleInputChange({ target: { name: 'OpsOrderGuid', value: selectedValue } })}
              placeholder={dropdownLoading ? t('loading') : t('select')}
              disabled={isLoading || dropdownLoading}
              invalid={!!errors.OpsOrderGuid}
              displayKey="Name"
              valueKey="OpsOrderGuid"
              emptyText={t('No options found')}
              onSearchTermChange={fetchOrderOptionsByOrderNo}
            />
            {errors.OpsOrderGuid && <div className="invalid-feedback d-block">{errors.OpsOrderGuid}</div>}
          </CCol>
          <CCol md={6}>
            <CFormLabel htmlFor="OpsShipmentStatusGuid">{t('shipmentStatus')}</CFormLabel>
            <CFormSelect
              id="OpsShipmentStatusGuid"
              name="OpsShipmentStatusGuid"
              value={formData.OpsShipmentStatusGuid}
              onChange={handleInputChange}
              invalid={!!errors.OpsShipmentStatusGuid}
              disabled={isLoading || dropdownLoading}
            >
              <option value="">{dropdownLoading ? t('loading') : t('select')}</option>
              {shipmentStatusOptions.map((item) => (
                <option key={item.ShipmentStatusGuid} value={item.ShipmentStatusGuid}>
                  {item.Name}
                </option>
              ))}
            </CFormSelect>
            {errors.OpsShipmentStatusGuid && <div className="invalid-feedback d-block">{errors.OpsShipmentStatusGuid}</div>}
          </CCol>
        </CRow>
        <CRow className="mb-3">
          <CCol md={6}>
            <CFormLabel htmlFor="ProductShippedQuantity">{t('productShippedQuantity')}</CFormLabel>
            <div className={`price-input-group ${errors.ProductShippedQuantity ? 'is-invalid' : ''}`}>
              <div className="price-input-section">
                <CFormInput
                  type="text"
                  id="shippedWhole"
                  className="price-whole"
                  value={quantityInputs.shippedWhole}
                  onChange={(e) => handleQuantityInputChange('shipped', 'whole', e.target.value)}
                  onKeyDown={(e) => handleQuantityKeyDown(e, 'shipped', 'whole')}
                  onFocus={() => handleQuantityFocus('shipped', 'whole')}
                  placeholder="0"
                  disabled={isLoading}
                />
                <span className="decimal-separator">,</span>
                <CFormInput
                  type="text"
                  id="shippedDecimal"
                  className="price-decimal"
                  value={quantityInputs.shippedDecimal}
                  onChange={(e) => handleQuantityInputChange('shipped', 'decimal', e.target.value)}
                  onKeyDown={(e) => handleQuantityKeyDown(e, 'shipped', 'decimal')}
                  onFocus={() => handleQuantityFocus('shipped', 'decimal')}
                  onBlur={() => handleQuantityBlur('shipped', 'decimal')}
                  placeholder="00"
                  maxLength="2"
                  disabled={isLoading}
                />
              </div>
            </div>
            {errors.ProductShippedQuantity && <div className="invalid-feedback d-block">{errors.ProductShippedQuantity}</div>}
          </CCol>
          <CCol md={6}>
            <CFormLabel htmlFor="PrdUnitGuid">{t('unit')}</CFormLabel>
            <CFormSelect
              id="PrdUnitGuid"
              name="PrdUnitGuid"
              value={formData.PrdUnitGuid}
              onChange={handleInputChange}
              invalid={!!errors.PrdUnitGuid}
              disabled={isLoading || dropdownLoading}
            >
              <option value="">{dropdownLoading ? t('loading') : t('select')}</option>
              {unitOptions.map((item) => (
                <option key={item.PrdUnitGuid} value={item.PrdUnitGuid}>
                  {item.UnitName}
                </option>
              ))}
            </CFormSelect>
            {errors.PrdUnitGuid && <div className="invalid-feedback d-block">{errors.PrdUnitGuid}</div>}
          </CCol>
        </CRow>
        <CRow className="mb-3">
          <CCol md={4}>
            <CFormLabel htmlFor="netWeightWhole">{t('netWeight') || 'Net Weight'}</CFormLabel>
            <div className={`price-input-group ${errors.NetWeight ? 'is-invalid' : ''}`}>
              <div className="price-input-section">
                <CFormInput
                  type="text"
                  id="netWeightWhole"
                  className="price-whole"
                  value={quantityInputs.netWeightWhole}
                  onChange={(e) => handleQuantityInputChange('netWeight', 'whole', e.target.value)}
                  onKeyDown={(e) => handleQuantityKeyDown(e, 'netWeight', 'whole')}
                  onFocus={() => handleQuantityFocus('netWeight', 'whole')}
                  placeholder="0"
                  disabled={isLoading}
                />
                <span className="decimal-separator">,</span>
                <CFormInput
                  type="text"
                  id="netWeightDecimal"
                  className="price-decimal"
                  value={quantityInputs.netWeightDecimal}
                  onChange={(e) => handleQuantityInputChange('netWeight', 'decimal', e.target.value)}
                  onKeyDown={(e) => handleQuantityKeyDown(e, 'netWeight', 'decimal')}
                  onFocus={() => handleQuantityFocus('netWeight', 'decimal')}
                  onBlur={() => handleQuantityBlur('netWeight', 'decimal')}
                  placeholder="00"
                  maxLength="2"
                  disabled={isLoading}
                />
              </div>
            </div>
            {errors.NetWeight && <div className="invalid-feedback d-block">{errors.NetWeight}</div>}
          </CCol>
          <CCol md={4}>
            <CFormLabel htmlFor="grossWeightWhole">{t('grossWeight') || 'Gross Weight'}</CFormLabel>
            <div className={`price-input-group ${errors.GrossWeight ? 'is-invalid' : ''}`}>
              <div className="price-input-section">
                <CFormInput
                  type="text"
                  id="grossWeightWhole"
                  className="price-whole"
                  value={quantityInputs.grossWeightWhole}
                  onChange={(e) => handleQuantityInputChange('grossWeight', 'whole', e.target.value)}
                  onKeyDown={(e) => handleQuantityKeyDown(e, 'grossWeight', 'whole')}
                  onFocus={() => handleQuantityFocus('grossWeight', 'whole')}
                  placeholder="0"
                  disabled={isLoading}
                />
                <span className="decimal-separator">,</span>
                <CFormInput
                  type="text"
                  id="grossWeightDecimal"
                  className="price-decimal"
                  value={quantityInputs.grossWeightDecimal}
                  onChange={(e) => handleQuantityInputChange('grossWeight', 'decimal', e.target.value)}
                  onKeyDown={(e) => handleQuantityKeyDown(e, 'grossWeight', 'decimal')}
                  onFocus={() => handleQuantityFocus('grossWeight', 'decimal')}
                  onBlur={() => handleQuantityBlur('grossWeight', 'decimal')}
                  placeholder="00"
                  maxLength="2"
                  disabled={isLoading}
                />
              </div>
            </div>
            {errors.GrossWeight && <div className="invalid-feedback d-block">{errors.GrossWeight}</div>}
          </CCol>
          <CCol md={4}>
            <CFormLabel htmlFor="Lot">{t('lot') || 'Lot'}</CFormLabel>
            <CFormInput
              type="text"
              id="Lot"
              name="Lot"
              value={formData.Lot}
              onChange={handleInputChange}
              invalid={!!errors.Lot}
              placeholder={t('enterLot') || 'Enter lot'}
              disabled={isLoading}
            />
            {errors.Lot && <div className="invalid-feedback d-block">{errors.Lot}</div>}
          </CCol>
        </CRow>
        <CRow className="mb-3">
          <CCol md={4}>
            <CFormLabel htmlFor="BillingCompany">{t('billingCompany')}</CFormLabel>
            <CFormInput
              type="text"
              id="BillingCompany"
              name="BillingCompany"
              value={formData.BillingCompany}
              onChange={handleInputChange}
              invalid={!!errors.BillingCompany}
              placeholder={t('enterBillingCompany')}
              disabled={isLoading}
            />
            {errors.BillingCompany && <div className="invalid-feedback d-block">{errors.BillingCompany}</div>}
          </CCol>
          <CCol md={4}>
            <CFormLabel htmlFor="ShipmentDatetime">{t('shipmentDatetime')}</CFormLabel>
            <CFormInput
              type="date"
              id="ShipmentDatetime"
              name="ShipmentDatetime"
              value={formatDateForInput(formData.ShipmentDatetime)}
              onChange={handleInputChange}
              invalid={!!errors.ShipmentDatetime}
              disabled={isLoading}
            />
            {errors.ShipmentDatetime && <div className="invalid-feedback d-block">{errors.ShipmentDatetime}</div>}
          </CCol>
          <CCol md={4}>
            <CFormLabel htmlFor="DeliveryNo">{t('deliveryNo')}</CFormLabel>
            <CFormInput
              type="text"
              id="DeliveryNo"
              name="DeliveryNo"
              value={formData.DeliveryNo}
              onChange={handleInputChange}
              invalid={!!errors.DeliveryNo}
              placeholder={t('enterDeliveryNo')}
              disabled={isLoading}
            />
            {errors.DeliveryNo && <div className="invalid-feedback d-block">{errors.DeliveryNo}</div>}
          </CCol>
        </CRow>
        <CRow className="mb-3">
          <CCol md={4}>
            <CFormLabel htmlFor="RollCount">{t('shipmentRollCount')}</CFormLabel>
            <CFormInput
              type="number"
              min="0"
              step="1"
              id="RollCount"
              name="RollCount"
              value={formData.RollCount}
              onChange={handleInputChange}
              invalid={!!errors.RollCount}
              placeholder={t('enterShipmentRollCount')}
              disabled={isLoading}
            />
            {errors.RollCount && <div className="invalid-feedback d-block">{errors.RollCount}</div>}
          </CCol>
          <CCol md={4}>
            <CFormLabel htmlFor="OpsSupplierGuid">Fason Dokuma Tedarikçisi</CFormLabel>
            <CFormSelect
              id="OpsSupplierGuid"
              name="OpsSupplierGuid"
              value={formData.OpsSupplierGuid}
              onChange={handleInputChange}
              invalid={!!errors.OpsSupplierGuid}
              disabled={isLoading || dropdownLoading}
            >
              <option value="">{dropdownLoading ? t('loading') : t('select')}</option>
              {supplierOptions.map((item) => (
                <option key={item.SupplierId} value={item.SupplierId}>
                  {item.SupplierName}
                </option>
              ))}
            </CFormSelect>
            {errors.OpsSupplierGuid && <div className="invalid-feedback d-block">{errors.OpsSupplierGuid}</div>}
          </CCol>
          <CCol md={4}>
            <CFormLabel htmlFor="Description">{t('description')}</CFormLabel>
            <CFormInput
              type="text"
              id="Description"
              name="Description"
              value={formData.Description}
              onChange={handleInputChange}
              invalid={!!errors.Description}
              placeholder={t('enterDescription')}
              disabled={isLoading}
            />
            {errors.Description && <div className="invalid-feedback d-block">{errors.Description}</div>}
          </CCol>
        </CRow>
        <CRow>
          <CCol md={12} className="d-flex gap-2">
            <CButton color="primary" type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <CSpinner size="sm" className="me-2" />
                  {getButtonText()}
                </>
              ) : (
                <>
                  <CIcon icon={cilSave} className="me-2" />
                  {getButtonText()}
                </>
              )}
            </CButton>
            <CButton color="secondary" variant="outline" onClick={handleCancel} disabled={isLoading}>
              <CIcon icon={cilX} className="me-2" />
              {t('cancel')}
            </CButton>
          </CCol>
        </CRow>
      </CForm>
    </div>
  );
};

ShipmentInputs.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  submitResponse: PropTypes.object,
  initialData: PropTypes.object,
  isEdit: PropTypes.bool,
};

export default ShipmentInputs; 
